import { useContext, useEffect, useRef, useState } from "react";
import useTranslator from "../../hooks/useTranslator";
import TenantContext from "../../context/tenant/TenantContext";
import AuthContext from "../../context/auth/AuthContext";
import { haveValue, toast } from "../../helper/common";
import {
  Slider,
  Badge,
  Button,
  Checkbox,
  Icon,
  Menu,
  Skeleton,
  TextField,
} from "../../components/design";
import "./tenantKelify.css";

const TenantKelify = () => {
  const { user, loadUser } = useContext(AuthContext);
  const { shareProfileFromKelify, loadSharedProfiles } =
    useContext(TenantContext);
  const [url, setUrl] = useState("");
  const { _t } = useTranslator();

  const shareUserProfile = async (payload) => {
    try {
      const res = await shareProfileFromKelify(payload);
      if (res?.success) {
        toast.success(res?.message);
        const shareLink = document.getElementById("shareProfileLink");
        if (shareLink) {
          await loadSharedProfiles(user?._id, "full");
          shareLink.click();
        }
      }
    } catch (error) {
      console.log("Error down", error);
      toast.error(_t("failedToShareProfile"));
    }
  };

  useEffect(() => {
    if (haveValue(user)) {
      const params = new URLSearchParams(window.location.search);
      const source_url = params.get("source_url");
      const agency_id = params.get("agency_id");

      if (source_url && agency_id) {
        try {
          const decodedUrl = atob(source_url);
          const payload = {
            kelify_agency_id: agency_id,
            source_url: decodedUrl,
          };
          shareUserProfile(payload);
        } catch (error) {
          console.error("Invalid Base64 string", error);
        }
      }

      // Remove query parameters from the URL
      params.delete("source_url");
      params.delete("agency_id");

      const newUrl =
        window.location.pathname +
        (params.toString() ? `?${params.toString()}` : "");

      // Update the URL in the browser without reload
      window.history.replaceState({}, document.title, newUrl);
      const kelifyId = user?.referredId?.agency_id?.kelify_id;

      let iframeUrl = "https://kelify.com/embed/search";
      if (kelifyId) {
        iframeUrl += `?agency_id=${kelifyId}`;
      }

      setUrl(iframeUrl);
    } else {
      loadUser("tenant");
    }
  }, [user]);

  // ==== STATES ====
  const [searchVal, setSearchVal] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [isSelecting, setIsSelecting] = useState(false);

  const serviceRef = useRef(null);

  const MIN_RANGE = 0;
  const MAX_RANGE = 5000;
  const GAP = 1;
  const [min, setMin] = useState(null);
  const [max, setMax] = useState(null);
  const [tempMin, setTempMin] = useState(MIN_RANGE);
  const [tempMax, setTempMax] = useState(MAX_RANGE);
  const [lat, setLat] = useState(null);
  const [lng, setLng] = useState(null);

  const [selectedBHK, setSelectedBHK] = useState([]);
  const [bathroom, setBathroom] = useState([]);

  const bhkOptions = ["0", "1", "2", "3", "4"];
  const bathroomOptions = ["1", "2", "3"];

  // ==== HANDLERS ====
  const toggleBHK = (option) => {
    setSelectedBHK((prev) =>
      prev.includes(option)
        ? prev.filter((b) => b !== option)
        : [...prev, option]
    );
  };

  const handleSetBudget = () => {
    // If full range selected (not customized), reset selection
    if (tempMin === MIN_RANGE && tempMax === MAX_RANGE) {
      setMin(null);
      setMax(null);
    } else {
      setMin(tempMin);
      setMax(tempMax);
    }
  };

  const toggleBathroom = (option) => {
    setBathroom((prev) =>
      prev.includes(option)
        ? prev.filter((b) => b !== option)
        : [...prev, option]
    );
  };

  // which filter panel is open: "price" | "bhk" | "bathroom" | null
  const [openPanel, setOpenPanel] = useState(null);
  const toolbarRef = useRef(null);

  useEffect(() => {
    if (!openPanel) return undefined;
    const onDown = (e) => {
      if (!toolbarRef.current?.contains(e.target)) setOpenPanel(null);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpenPanel(null);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [openPanel]);

  const togglePanel = (name) =>
    setOpenPanel((prev) => (prev === name ? null : name));

  const getBudgetText = () => {
    if (min === null || max === null) return _t("selectRange");
    return `${min}€ - ${max}€`;
  };

  // ==== UPDATE IFRAME URL ====
  useEffect(() => {
    if (url) {
      const queryParams = new URLSearchParams();

      // Price filters
      if (min !== null) queryParams.set("min_price", min);
      if (max !== null) queryParams.set("max_price", max);
      if (lat !== null) queryParams.set("lat", lat);
      if (lng !== null) queryParams.set("lng", lng);

      // BHK filters
      if (selectedBHK.length > 0) {
        selectedBHK.forEach((bhk) => {
          queryParams.append("rooms[]", bhk);
        });
      }

      // Bathroom filters
      if (bathroom.length > 0) {
        bathroom.forEach((b) => {
          queryParams.append("bathrooms[]", b);
        });
      }

      const newUrl = `https://kelify.com/embed/search?${queryParams.toString()}`;
      setUrl(newUrl);
    }
  }, [min, max, selectedBHK, bathroom, lng, lat]);

  useEffect(() => {
    if (!window.google || !searchVal || isSelecting) {
      setLng(null);
      setLat(null);
      return setSuggestions([]);
    }

    if (!serviceRef.current) {
      serviceRef.current = new window.google.maps.places.AutocompleteService();
    }

    serviceRef.current.getPlacePredictions(
      {
        input: searchVal,
        componentRestrictions: { country: "es" },
      },
      (predictions, status) => {
        if (
          status === window.google.maps.places.PlacesServiceStatus.OK &&
          predictions
        ) {
          setSuggestions(predictions);
        } else {
          setSuggestions([]);
        }
      }
    );
  }, [searchVal]);

  const handleSelect = (description) => {
    setIsSelecting(true);
    setSearchVal(description);
    setSuggestions([]);
    setLng(null);
    setLat(null);

    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address: description }, (results, status) => {
      if (status === "OK" && results[0]) {
        const { lat, lng } = results[0].geometry.location;
        setLng(lng());
        setLat(lat());
      } else {
        console.error("Geocode failed:", status);
      }
    });

    setTimeout(() => setIsSelecting(false), 100);
  };

  const filterButton = (name, label, active, count) => (
    <Button
      variant={active ? "primary" : "outline"}
      size="md"
      aria-haspopup="true"
      aria-expanded={openPanel === name}
      onClick={() => togglePanel(name)}
      iconEnd={<Icon name="chevronDown" size={16} />}
    >
      {label}
      {count > 0 && <Badge tone="lime">{count}</Badge>}
    </Button>
  );

  return (
    <div className="main-page-wrapper kelifyPage mb-4">
      <div className="kelifyPage__toolbar" ref={toolbarRef}>
        <div className="kelifyPage__collab">
          <span className="kelifyPage__collabText">
            {_t("inCollaborationWith")}
          </span>
          <img
            src="/images/kelify-logo.png"
            className="kelifyPage__collabLogo"
            alt="Kelify"
          />
        </div>

        <div className="kelifyPage__search">
          <TextField
            id="kelify-search"
            size="sm"
            icon={<Icon name="search" size={18} />}
            value={searchVal}
            onChange={(e) => setSearchVal(e.target.value)}
            placeholder={_t("searchProperty")}
            aria-label={_t("searchProperty")}
            autoComplete="off"
            trailing={
              (searchVal || suggestions.length > 0) && (
                <Button
                  variant="ghost"
                  size="sm"
                  iconOnly
                  aria-label="Clear"
                  iconStart={<Icon name="close" size={16} />}
                  onClick={() => {
                    setSearchVal("");
                    setSuggestions([]);
                  }}
                />
              )
            }
          />
          {suggestions.length > 0 && (
            <Menu
              className="kelifyPage__dropdown kelifyPage__suggestions"
              title={_t("results")}
              items={suggestions.map((item) => ({
                key: item.place_id,
                label: (
                  <span className="kelifyPage__ellipsis">
                    {item.description}
                  </span>
                ),
                onClick: () => handleSelect(item.description),
              }))}
            />
          )}
        </div>

        <div className="kelifyPage__filters">
          {/* ==== BUDGET FILTER ==== */}
          <div className="kelifyPage__filter">
            {filterButton("price", getBudgetText(), min !== null, 0)}
            {openPanel === "price" && (
              <div className="kelifyPage__dropdown kelifyPage__dropdown--end kelifyPage__panel kelifyPage__panel--price">
                <div className="kelifyPage__range">
                  <div
                    className="kelifyPage__rangeFill"
                    style={{
                      left: `${(tempMin / MAX_RANGE) * 100}%`,
                      right: `${100 - (tempMax / MAX_RANGE) * 100}%`,
                    }}
                  />
                  <Slider
                    className="kelifyPage__rangeInput"
                    min={MIN_RANGE}
                    max={MAX_RANGE}
                    value={tempMin}
                    aria-label="Min"
                    onChange={(e) =>
                      setTempMin(
                        Math.min(Number(e.target.value), tempMax - GAP)
                      )
                    }
                  />
                  <Slider
                    className="kelifyPage__rangeInput"
                    min={MIN_RANGE}
                    max={MAX_RANGE}
                    value={tempMax}
                    aria-label="Max"
                    onChange={(e) =>
                      setTempMax(
                        Math.max(Number(e.target.value), tempMin + GAP)
                      )
                    }
                  />
                </div>
                <div className="kelifyPage__priceRow">
                  <TextField
                    id="kelify-min"
                    type="number"
                    size="sm"
                    value={tempMin}
                    aria-label="Min"
                    onChange={(e) =>
                      setTempMin(
                        Math.min(Number(e.target.value), tempMax - GAP)
                      )
                    }
                  />
                  <span>{_t("to")}</span>
                  <TextField
                    id="kelify-max"
                    type="number"
                    size="sm"
                    value={tempMax}
                    aria-label="Max"
                    onChange={(e) =>
                      setTempMax(
                        Math.max(Number(e.target.value), tempMin + GAP)
                      )
                    }
                  />
                  <Button size="sm" onClick={handleSetBudget}>
                    {_t("set")}
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* ==== BHK TYPE ==== */}
          <div className="kelifyPage__filter">
            {filterButton(
              "bhk",
              _t("bhkType"),
              selectedBHK.length > 0,
              selectedBHK.length
            )}
            {openPanel === "bhk" && (
              <div className="kelifyPage__dropdown kelifyPage__panel">
                {bhkOptions.map((option) => (
                  <Checkbox
                    key={option}
                    className="kelifyPage__option"
                    checked={selectedBHK.includes(option)}
                    onChange={() => toggleBHK(option)}
                    label={`${option} ${_t("bhk")}`}
                  />
                ))}
              </div>
            )}
          </div>

          {/* ==== BATHROOMS ==== */}
          <div className="kelifyPage__filter">
            {filterButton(
              "bathroom",
              _t("bathrooms"),
              bathroom.length > 0,
              bathroom.length
            )}
            {openPanel === "bathroom" && (
              <div className="kelifyPage__dropdown kelifyPage__dropdown--end kelifyPage__panel">
                {bathroomOptions.map((opt) => (
                  <Checkbox
                    key={opt}
                    className="kelifyPage__option"
                    checked={bathroom.includes(opt)}
                    onChange={() => toggleBathroom(opt)}
                    label={`${opt} ${_t("bathroom")}`}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ==== IFRAME ==== */}
      <div className="kelifyPage__frame">
        {url ? (
          <iframe
            src={url}
            title="Embedded Page"
            className="kelifyPage__iframe"
          />
        ) : (
          <Skeleton width="100%" height="100%" radius="lg" />
        )}
      </div>
    </div>
  );
};

export default TenantKelify;
